import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Icon from "./Icon";
import AppUtils from "@src/utils/appUtils";
import useOutsideClick from "@src/components/utility/OutsideClickHook";
import { useIsMobileWidth } from "@src/utils/mediaQuery";
import { HelpText } from "../utility/textFormatterHelpers";

const Tooltip = ({
  content = [],
  direction,
  small = false,
  absolute = true,
  darkMode = false,
  centered = false,
  rightPosition = 6,
}) => {
  const isMobile = useIsMobileWidth();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const handleClickOutside = () => setOpen(false);
  const tooltipRef = useOutsideClick(handleClickOutside);

  const toggleTooltip = () => {
    setOpen((prev) => !prev);
  };

  const [computedDirection, setComputedDirection] = useState(direction);
  useEffect(() => {
    const recalculateDirection = (direction, small, isMobile) => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;

      const tooltipWidth = !!small ? 192 : !!isMobile ? 208 : 320;
      const tooltipHeight = !!small ? 80 : 100;

      const fitsRight =
        rect.left + tooltipWidth < document.documentElement.clientWidth;
      const fitsLeft = rect.right - tooltipWidth > 0;
      const fitsTop = rect.top - tooltipHeight > 0;
      const fitsBottom =
        rect.bottom + tooltipHeight < document.documentElement.clientHeight;

      let updated = direction;

      if (direction.includes("right") && !fitsRight && fitsLeft) {
        updated = updated.replace("right", "left");
      } else if (direction.includes("left") && !fitsLeft && fitsRight) {
        updated = updated.replace("left", "right");
      }

      if (direction.includes("up") && !fitsTop && fitsBottom) {
        updated = updated.replace("up", "down");
      } else if (direction.includes("down") && !fitsBottom && fitsTop) {
        updated = updated.replace("down", "up");
      }
      return updated;
    };

    const newDirection = recalculateDirection(direction, small, isMobile);
    setComputedDirection(newDirection);
  }, [isMobile, direction, small, setComputedDirection]);

  return (
    <div
      ref={(node) => {
        tooltipRef.current = node;
        wrapperRef.current = node;
      }}
      className={`group w-lg ${absolute ? `absolute right-${rightPosition}` : "relative"} ${centered ? "content-center" : ""} `}
    >
      <div onClick={toggleTooltip}>
        <Icon
          name={"info"}
          backgroundColor={"transparent"}
          size={"lg"}
          padding={false}
          textColor={darkMode ? "white" : "black"}
          clickable={true}
          className="cursor-help"
        />
      </div>
      <div
        className={`${
          open ? "visible" : "invisible group-hover:visible"
        } absolute transition-opacity duration-200 ease-in-out 
      ${computedDirection === "up-right" ? "bottom-6 left-4" : ""}
      ${computedDirection === "up-left" ? "bottom-6 right-4" : ""}
      ${computedDirection === "down-right" ? "top-6 left-4" : ""}
      ${computedDirection === "down-left" ? "top-6 right-4" : ""}
      ${
        small ? "w-auto text-xs min-w-48" : "text-sm w-80 min-w-60"
      } rounded-md whitespace-normal font-normal z-51 tracking-wide`}
      >
        <HelpText>
          {content.map((value = "", idx) => (
            <div
              key={idx}
              className=""
              style={{ width: `${small ? 192 : isMobile ? 208 : 320}` }}
            >
              {value}
            </div>
          ))}
        </HelpText>
      </div>
    </div>
  );
};

export const tooltipControls = {
  content: {
    propType: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.element),
      PropTypes.arrayOf(PropTypes.string),
    ]),
    description: "Tooltip content (array of strings or elements)",
    disable: true,
  },
  direction: {
    propType: PropTypes.string,
    controlType: "select",
    options: ["up-right", "up-left", "down-right", "down-left"],
    description: "Tooltip display direction",
    defaultValue: "up-right",
  },
  absolute: {
    propType: PropTypes.bool,
    controlType: "boolean",
    description: "Whether tooltip uses absolute positioning",
    defaultValue: true,
  },
  centered: {
    propType: PropTypes.bool,
    controlType: "boolean",
    description: "Whether tooltip is centered in its div",
    defaultValue: false,
  },
  darkMode: {
    propType: PropTypes.bool,
    controlType: "boolean",
    description: "Dark mode styling",
    defaultValue: false,
  },
  small: {
    propType: PropTypes.bool,
    controlType: "boolean",
    description: "Small variant of tooltip",
    defaultValue: false,
  },
};

Tooltip.propTypes = AppUtils.getPropTypeFromControls(tooltipControls);

export default Tooltip;
